import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getRouteMutationKey } from '.';
import { stopService } from '../../../../application/StopService';
import { Stop } from '../../../../domain/stops/Stop';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';

interface UseDeleteStopReportProps {
  stop: Stop;
}

type UseDeleteStopReportResponseProps = ServiceResponse<({ stop }: UseDeleteStopReportProps) => void, void> & {
  reset: () => void;
};

// Hooks act as 'Adapter' layer.
export const useDeleteStopReport = (): UseDeleteStopReportResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const queryClient = useQueryClient();

  const { isLoading, isSuccess, isError, mutate, reset } = useMutation<Stop, unknown, { stop: Stop }>(
    ({ stop }: { stop: Stop }): Promise<Stop> => stopService.discardReport({ stop }),
    {
      onSuccess: (updatedStop) => {
        queryClient.invalidateQueries(getRouteMutationKey({ routeId: updatedStop.route_id }));
      },
      onError: (err: any, _variables: any, context: any) => {
        console.info(`Delete Stop Report Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  const discardReport = ({ stop }: UseDeleteStopReportProps) => {
    mutate({ stop });
  };

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: discardReport,
    reset,
  };
};
